export const membershipUserId = (user) => user?.user_id ?? user?.id;

export const membershipKey = (user) =>
  `${membershipUserId(user) ?? user.user_code}::${user.company_id ?? ""}`;

// The company_id sent for a user in the group payload is the company that
// user belongs to (2026-10-07, user request) -- not a company picked under
// Companies & Role Assignment. The group's first assigned company is used
// only when the user's own company is unknown.
export const membershipCompanyId = (homeCompanyId, assignedCompanyIds = []) => {
  if (homeCompanyId != null && String(homeCompanyId) !== "") return String(homeCompanyId);
  return assignedCompanyIds.length ? String(assignedCompanyIds[0]) : null;
};

export const hasExistingMembership = (user, members, assignedCompanyIds = []) =>
  members.some((member) => {
    // Legacy edit responses may omit the membership company. With a sole
    // assigned company, exclude an existing user from duplicate selection
    // without changing their saved company or inferring it from home company.
    const companyId = member.company_id ??
      (assignedCompanyIds.length === 1 ? assignedCompanyIds[0] : null);
    if (companyId == null || user.company_id == null ||
      String(companyId) !== String(user.company_id)) return false;
    const memberId = membershipUserId(member);
    const userId = membershipUserId(user);
    if (memberId != null && userId != null && String(memberId) === String(userId)) return true;
    // An explicit user_id is authoritative; a plain id in an edit response
    // can identify the membership record. Older responses use user_code.
    if (member.user_id != null && userId != null) return false;
    if (member.home_company_id != null && user.home_company_id != null &&
      String(member.home_company_id) !== String(user.home_company_id)) return false;
    return Boolean(member.user_code && user.user_code &&
      String(member.user_code).trim() === String(user.user_code).trim());
  });

// Add Users has no company choice, so a person already in the group must
// not be offered again under another company. Unlike hasExistingMembership
// this ignores which company the saved membership is under (it may differ
// from the derived one, or be missing in legacy responses).
export const isGroupMember = (user, members) =>
  members.some((member) => {
    const memberId = membershipUserId(member);
    const userId = membershipUserId(user);
    if (memberId != null && userId != null && String(memberId) === String(userId)) return true;
    if (member.user_id != null && userId != null) return false;
    if (member.home_company_id != null && user.home_company_id != null &&
      String(member.home_company_id) !== String(user.home_company_id)) return false;
    return Boolean(member.user_code && user.user_code &&
      String(member.user_code).trim() === String(user.user_code).trim());
  });

export const hasMembershipCompany = (user, companies) =>
  user.company_id != null && companies.some((company) =>
    String(company.company_id ?? company.id) === String(user.company_id));

export const departmentsForMembership = (departments, companyId) =>
  companyId == null ? [] : departments;

export const normalizeDepartmentOptions = (data) => {
  const records = Array.isArray(data) ? data : data?.results ?? [];
  // Departments are schema-wide; membership company controls access to
  // employees, not which department IDs may be assigned to that membership.
  return records.map((department) => ({
    ...department,
    id: department.id ?? department.department_id,
    department: department.department ?? department.department_name ?? department.name,
  })).filter((department) => department.id != null);
};

export const hasCompanyDepartmentScope = (user, departments) =>
  user.company_id != null && (user.scope !== "additional" ||
    (user.departments?.length > 0 && user.departments.every((department) =>
      departmentsForMembership(departments, user.company_id).some((option) =>
        String(option.id) === String(department.id)))));

export const isActiveRecord = (record) => record?.is_active !== false &&
  record?.isactive !== false && record?.is_deleted !== true &&
  !["inactive", "deleted"].includes(String(record?.status ?? record?.role_status ?? "").toLowerCase());
